import React, { useState, useRef } from 'react';
import { 
  User, Mail, Phone, Car, FileText, Upload, Camera, 
  ChevronRight, ChevronLeft, Check, AlertCircle, Truck,
  CreditCard, Shield, MapPin
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const DriversOnboarding = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: 'Ottawa',
    province: 'ON',
    postalCode: '',
    
    // License Info
    licenseNumber: '',
    licenseExpiry: '',
    licenseClass: '',
    
    // Vehicle Info
    vehicleType: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleColor: '',
    licensePlate: '',
    
    // Documents
    licensePhoto: null,
    insurancePhoto: null,
    vehiclePhoto: null,
    profilePhoto: null,
    
    // Banking
    bankName: '',
    accountNumber: '',
    transitNumber: '',
    institutionNumber: '',
  });
  
  const [uploadedFiles, setUploadedFiles] = useState({
    licensePhoto: null,
    insurancePhoto: null,
    vehiclePhoto: null,
    profilePhoto: null,
  });

  const fileInputRefs = {
    licensePhoto: useRef(null),
    insurancePhoto: useRef(null),
    vehiclePhoto: useRef(null),
    profilePhoto: useRef(null),
  };

  const steps = [
    { id: 1, title: 'Personal Info', icon: User },
    { id: 2, title: 'License', icon: CreditCard },
    { id: 3, title: 'Vehicle', icon: Car },
    { id: 4, title: 'Documents', icon: FileText },
    { id: 5, title: 'Banking', icon: Shield },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles(prev => ({ ...prev, [field]: reader.result }));
        setFormData(prev => ({ ...prev, [field]: file }));
      };
      reader.readAsDataURL(file);
      toast.success(`${field.replace('Photo', '')} uploaded successfully`);
    }
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    toast.success('Application submitted successfully! We will review and contact you within 24-48 hours.');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-gray-100 overflow-hidden mb-4">
                {uploadedFiles.profilePhoto ? (
                  <img src={uploadedFiles.profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRefs.profilePhoto.current?.click()}
              >
                <Camera className="w-4 h-4 mr-2" />
                Upload Photo
              </Button>
              <input
                ref={fileInputRefs.profilePhoto}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileUpload('profilePhoto', e)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="John"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="john@example.com"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 (613) 555-0123"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Street Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="123 Main Street"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Province</Label>
                <Input
                  value={formData.province}
                  onChange={(e) => handleInputChange('province', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Postal Code *</Label>
                <Input
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  placeholder="K1P 5N2"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-xl mb-6">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Please ensure your license is valid and not expired
              </p>
            </div>

            <div>
              <Label>Driver&apos;s License Number *</Label>
              <Input
                value={formData.licenseNumber}
                onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                placeholder="A1234-56789-01234"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Expiry Date *</Label>
                <Input
                  type="date"
                  value={formData.licenseExpiry}
                  onChange={(e) => handleInputChange('licenseExpiry', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>License Class *</Label>
                <Select
                  value={formData.licenseClass}
                  onValueChange={(value) => handleInputChange('licenseClass', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="G">Class G - Full License</SelectItem>
                    <SelectItem value="G2">Class G2</SelectItem>
                    <SelectItem value="G1">Class G1</SelectItem>
                    <SelectItem value="M">Class M - Motorcycle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label>Vehicle Type *</Label>
              <Select
                value={formData.vehicleType}
                onValueChange={(value) => handleInputChange('vehicleType', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select vehicle type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                  <SelectItem value="motorcycle">Motorcycle</SelectItem>
                  <SelectItem value="bicycle">Bicycle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Make *</Label>
                <Input
                  value={formData.vehicleMake}
                  onChange={(e) => handleInputChange('vehicleMake', e.target.value)}
                  placeholder="Toyota"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Vehicle Model *</Label>
                <Input
                  value={formData.vehicleModel}
                  onChange={(e) => handleInputChange('vehicleModel', e.target.value)}
                  placeholder="Corolla"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Year *</Label>
                <Input
                  type="number"
                  value={formData.vehicleYear}
                  onChange={(e) => handleInputChange('vehicleYear', e.target.value)}
                  placeholder="2020"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Vehicle Color *</Label>
                <Input
                  value={formData.vehicleColor}
                  onChange={(e) => handleInputChange('vehicleColor', e.target.value)}
                  placeholder="Silver"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>License Plate Number *</Label>
              <Input
                value={formData.licensePlate}
                onChange={(e) => handleInputChange('licensePlate', e.target.value)}
                placeholder="ABCD 123"
                className="mt-1"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-yellow-50 rounded-xl mb-6">
              <p className="text-sm text-yellow-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Upload clear photos of your documents. Accepted formats: JPG, PNG, PDF
              </p>
            </div>

            {/* License Photo */}
            <div>
              <Label>Driver&apos;s License (Front & Back) *</Label>
              <div 
                className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => fileInputRefs.licensePhoto.current?.click()}
              >
                {uploadedFiles.licensePhoto ? (
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-600">License uploaded</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRefs.licensePhoto}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => handleFileUpload('licensePhoto', e)}
              />
            </div>

            {/* Insurance Photo */}
            <div>
              <Label>Vehicle Insurance *</Label>
              <div 
                className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => fileInputRefs.insurancePhoto.current?.click()}
              >
                {uploadedFiles.insurancePhoto ? (
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-600">Insurance uploaded</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRefs.insurancePhoto}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => handleFileUpload('insurancePhoto', e)}
              />
            </div>

            {/* Vehicle Photo */}
            <div>
              <Label>Vehicle Photo *</Label>
              <div 
                className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => fileInputRefs.vehiclePhoto.current?.click()}
              >
                {uploadedFiles.vehiclePhoto ? (
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-600">Vehicle photo uploaded</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRefs.vehiclePhoto}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileUpload('vehiclePhoto', e)}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="p-4 bg-green-50 rounded-xl mb-6">
              <p className="text-sm text-green-700 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Your banking information is encrypted and secure
              </p>
            </div>

            <div>
              <Label>Bank Name *</Label>
              <Select
                value={formData.bankName}
                onValueChange={(value) => handleInputChange('bankName', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select your bank" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="td">TD Canada Trust</SelectItem>
                  <SelectItem value="rbc">RBC Royal Bank</SelectItem>
                  <SelectItem value="scotiabank">Scotiabank</SelectItem>
                  <SelectItem value="bmo">BMO Bank of Montreal</SelectItem>
                  <SelectItem value="cibc">CIBC</SelectItem>
                  <SelectItem value="national">National Bank</SelectItem>
                  <SelectItem value="desjardins">Desjardins</SelectItem>
                  <SelectItem value="tangerine">Tangerine</SelectItem>
                  <SelectItem value="simplii">Simplii Financial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Transit Number *</Label>
                <Input
                  value={formData.transitNumber}
                  onChange={(e) => handleInputChange('transitNumber', e.target.value)}
                  placeholder="12345"
                  maxLength={5}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">5 digits</p>
              </div>
              <div>
                <Label>Institution Number *</Label>
                <Input
                  value={formData.institutionNumber}
                  onChange={(e) => handleInputChange('institutionNumber', e.target.value)}
                  placeholder="123"
                  maxLength={3}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">3 digits</p>
              </div>
            </div>

            <div>
              <Label>Account Number *</Label>
              <Input
                value={formData.accountNumber}
                onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                placeholder="1234567890123"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">7-12 digits</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Truck className="w-8 h-8 text-green-500" />
            Driver Onboarding
          </h1>
          <p className="text-gray-500 mt-1">Complete your profile to start delivering</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    currentStep >= step.id 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <StepIcon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-xs mt-2 ${
                  currentStep >= step.id ? 'text-green-600 font-medium' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Form Card */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">
          {steps.find(s => s.id === currentStep)?.title}
        </h2>
        
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex gap-4 mt-8 pt-6 border-t">
          <Button 
            variant="outline" 
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex-1"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          {currentStep < 5 ? (
            <Button 
              onClick={nextStep}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
            >
              Submit Application
              <Check className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DriversOnboarding;
